import React, { createContext, useContext } from 'react'
import { hydrateRoot } from 'react-dom/client'

import { PlatformLogo } from '@f/elements/PlatformLogo/PlatformLogo'
import { PlatformLogoOnWhite } from '@f/elements/PlatformLogo/PlatformLogoOnWhite'

import { Domain } from '@f-fertighaus/applications/Next/pages/house-detail-page/HDPContent'

import { PageHeader } from './components'
import { PageHeaderStandard } from './PageHeaderStandard'

import './PageHeaderSSR.css'

export interface PageHeaderSSRProps {
  appearance:
    | 'logo_only'
    | 'standard'
    | 'logo_social'
    | 'logo_with_rating'
    | 'logo_with_rating_light'
  domain: Domain
  sales_agent_app_page?: boolean
  is_full_width?: boolean
  menu_items?: MenuItem[]
  pagetype: string
  country: 'de' | 'at'
}

export interface MenuItem {
  url: string
  title: string
  type: MenuItemType
  active: boolean
  className: string
  only_mobile: boolean
  sub_menu_items: SubMenuItem[]
}

export interface SubMenuItem {
  title: string
  label: string
  url: string
  tracking_id: string
  sub_menu_links: SubMenuLink[]
  className?: string
  force_new_column?: boolean
}

interface SubMenuLink {
  label: string
  url: string
}

export type MenuItemType =
  | 'house-list'
  | 'vendor-list'
  | 'financing'
  | 'learning'
  | 'user'
  | 'search'
  | 'mobile'
  | 'mobile-wissen'

const PageHeaderContext = createContext<PageHeaderSSRProps | null>(null)

export function usePageHeader() {
  const context = useContext(PageHeaderContext)

  if (!context) {
    throw new Error('usePageHeader must be used within PageHeaderProvider')
  }

  return context
}

export const PageHeaderSSR = (props: PageHeaderSSRProps) => {
  return (
    <PageHeaderContext.Provider value={props}>
      {{
        logo_only: (
          <PageHeader.Wrapper>
            <PageHeader.Logo domain={props.domain} country={props.country} />
          </PageHeader.Wrapper>
        ),
        logo_social: (
          <PageHeader.Wrapper>
            <PageHeader.Logo domain={props.domain} country={props.country} />
            <PageHeader.Social />
          </PageHeader.Wrapper>
        ),
        logo_with_rating: (
          <PageHeader.Wrapper>
            <PageHeader.Logo domain={props.domain} country={props.country} />
            <PageHeader.StarRating />
          </PageHeader.Wrapper>
        ),
        logo_with_rating_light: (
          <PageHeader.Wrapper>
            <div className='f-hidden:m f-hidden:l'>
              <PlatformLogoOnWhite domain={props.domain} />
            </div>
            <div className='f-hidden:s'>
              <PlatformLogo domain={props.domain} />
            </div>
            <PageHeader.StarRating />
          </PageHeader.Wrapper>
        ),
        standard: (
          <PageHeader.Wrapper>
            <span className='[ f-pos-rel ] js-logo'>
              <a
                className='[ f-pos-rel f-d-inbl f-p-v-010 ] PageHeader-logo-link'
                href='/'>
                <PageHeader.Logo
                  domain={props.domain}
                  country={props.country}
                />
              </a>
            </span>
            <PageHeaderStandard />
          </PageHeader.Wrapper>
        )
      }[props.appearance] || null}
    </PageHeaderContext.Provider>
  )
}

if (typeof window !== 'undefined' && window.mountHeader) {
  const container = window.mountHeader

  if (container) {
    hydrateRoot(container, <PageHeaderSSR {...window.pageHeaderProps} />)
  }
}
